import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * SearchableSelect — accessible searchable dropdown (no deps).
 * Props:
 *   value       string  currently selected id
 *   onChange    (id) => void
 *   options     [{id, label, sub?, color?}]
 *   placeholder string
 *   testId      data-testid prefix
 *   required    bool
 *   clearable   bool (default true)
 */
export function SearchableSelect({ value, onChange, options, placeholder = "Seleziona…", testId, required, clearable = true, disabled, size }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [highlight, setHighlight] = useState(0);

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.sub || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [q, options]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const o = filtered[highlight];
      if (o) { onChange(o.id); setOpen(false); }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`sp-input w-full flex items-center justify-between text-left ${size === "sm" ? "!py-1 text-xs" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className="truncate flex items-center gap-2 flex-1">
          {selected?.color && <span className="w-2 h-2 rounded-full" style={{ background: selected.color }} />}
          {selected ? (
            <>
              <span className="truncate">{selected.label}</span>
              {selected.sub && <span className="text-xs text-[color:var(--sp-text-muted)] truncate">· {selected.sub}</span>}
            </>
          ) : (
            <span className="text-[color:var(--sp-text-muted)]">{placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 ml-2">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="p-0.5 rounded-full hover:bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-muted)]"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
        </span>
      </button>

      {required && !selected && <input tabIndex={-1} aria-hidden className="sr-only" required value="" onChange={() => {}} />}

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-[color:var(--sp-border)] bg-white shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[color:var(--sp-border)] relative">
            <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setHighlight(0); }}
              onKeyDown={onKey}
              placeholder="Cerca…"
              className="sp-input !pl-8 !py-1 text-xs"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div ref={listRef} className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-[color:var(--sp-text-muted)] text-center">Nessun risultato</div>
            ) : (
              filtered.map((o, i) => {
                const isSel = o.id === value;
                const isHi = i === highlight;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => { onChange(o.id); setOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isHi ? "bg-[color:var(--sp-brand)]/10" : ""} ${isSel ? "font-black text-[color:var(--sp-brand)]" : ""}`}
                    data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                  >
                    {o.color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: o.color }} />}
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.sub && <span className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{o.sub}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
